import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { EMPTY_THEME, parseTheme, type Theme } from '@crew/shared';

const ThemeContext = createContext<Theme>(EMPTY_THEME);

export function useTheme(): Theme {
  return useContext(ThemeContext);
}

/**
 * Loads the optional local asset pack once at boot. A missing or broken pack is
 * not an error - the game just uses its built-in artwork.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(EMPTY_THEME);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/theme')
      .then((r) => (r.ok ? r.json() : null))
      .then((raw) => {
        if (cancelled || !raw) return;
        const parsed = parseTheme(raw);
        setTheme(parsed);
        for (const [suit, colour] of Object.entries(parsed.suitColors)) {
          document.documentElement.style.setProperty(`--${suit}`, colour);
        }
        if (parsed.background) {
          document.documentElement.style.setProperty(
            '--table-backdrop',
            `url("${parsed.background}")`
          );
          document.body.classList.add('has-theme-backdrop');
        }
      })
      .catch(() => {
        /* no pack installed */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
}
